import { useState, useEffect } from 'react';
import { useNavigate } from "react-router";
import React from "react";
import ComponentCard from "../common/ComponentCard";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Switch from "../form/switch/Switch";
import Button from "../ui/button/Button";
import Checkbox from "../form/input/Checkbox";
import DatePicker from "../form/date-picker";
import axios from "axios";
import { authService } from "../../services/authService";
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';

interface CouponFormValues {
    active: boolean;
    code: string;
    description: string;
    minValue: string;
    availableCoupons: string;
    hasExpiration: boolean;
    startDate: string;
    expirationDate: string;
    discountType: 'fixed' | 'percentage';
    discountValue: string;
    productId: string;
    offerId: string;
    global: boolean;
    offerList: string[];
}

interface EditCouponFormProps {
    couponId: string;
    productId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

// Função para formatar valor em reais
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value);
};

export default function EditCouponForm({ couponId, productId, onSuccess, onError }: EditCouponFormProps) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingCoupon, setLoadingCoupon] = useState(true);
    const [apiSuccess, setApiSuccess] = useState("");
    const [apiError, setApiError] = useState("");

    const [formValues, setFormValues] = useState<CouponFormValues>({
        active: true,
        code: '',
        description: '',
        minValue: '0',
        availableCoupons: '0',
        hasExpiration: false,
        startDate: new Date().toISOString().split('T')[0],
        expirationDate: new Date().toISOString().split('T')[0],
        discountType: 'fixed',
        discountValue: '0',
        productId: productId || '',
        offerId: '',
        global: true,
        offerList: []
    });

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=coupon`);
    };

    const [errors, setErrors] = useState<Record<string, string>>({});
    // Estados para produtos e ofertas
    const [productOffers, setProductOffers] = useState<Array<{ value: string, label: string }>>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
    const [selectAllOffers, setSelectAllOffers] = useState(false);

    // Carregar ofertas ao carregar o componente
    useEffect(() => {
        fetchProductOffers(formValues.productId);
    }, [formValues.productId]);

    // Função para buscar ofertas de um produto específico
    const fetchProductOffers = async (selectedProductId: string) => {
        if (!selectedProductId) return;

        try {
            setLoadingOffers(true);

            // Agora, vamos buscar as ofertas
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${selectedProductId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar o formato da resposta do backend
            if (response.data && response.data.offers) {
                // Criar opções para o dropdown com as ofertas do produto
                const offerOptions = response.data.offers.map((offer: any) => {
                    // Formatar o preço para exibição
                    const formattedPrice = offer.price ?
                        `R$ ${parseFloat(offer.price).toFixed(2).replace('.', ',')}` :
                        'Preço não disponível';

                    return {
                        value: offer.id,
                        label: `${offer.name} - ${formattedPrice}`
                    };
                });

                // Adicionar a opção "Qualquer oferta" no início da lista
                const allOptions = [
                    { value: 'any', label: ' Qualquer oferta' },
                    ...offerOptions
                ];

                setProductOffers(allOptions);
            }
        } catch (error) {
            console.error("Erro ao carregar ofertas do produto:", error);
        } finally {
            setLoadingOffers(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));

        // Limpar erros quando o campo é editado
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSwitchChange = (field: string, checked: boolean) => {
        setFormValues(prev => ({ ...prev, [field]: checked }));
    };

    const handleDiscountTypeChange = (type: 'fixed' | 'percentage') => {
        setFormValues(prev => ({ ...prev, discountType: type }));
    };

    const handleCheckboxChange = (checked: boolean) => {
        setFormValues(prev => ({
            ...prev,
            hasExpiration: checked,
            startDate: checked ? prev.startDate : new Date().toISOString().split('T')[0],
            expirationDate: checked ? prev.expirationDate : new Date().toISOString().split('T')[0],
        }));
    };

    const generateRandomCode = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 12; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        setFormValues(prev => ({ ...prev, code: result }));
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Validar campos obrigatórios
        if (!formValues.code) {
            newErrors.code = "O código do cupom é obrigatório";
        }

        // Validar oferta selecionada (global ou pelo menos uma oferta específica)
        if (!formValues.global && formValues.offerList.length === 0) {
            newErrors.offers = "Selecione pelo menos uma oferta ou 'Todas as ofertas'";
        }

        if (parseFloat(formValues.minValue.replace(/\D/g, '')) / 100 < 0) {
            newErrors.minValue = "O valor mínimo não pode ser negativo";
        }

        if (parseInt(formValues.availableCoupons) < 0) {
            newErrors.availableCoupons = "A quantidade de cupons disponíveis deve ser maior que zero";
        }

        if (formValues.discountType === 'percentage') {
            const percentageValue = parseInt(formValues.discountValue);
            if (percentageValue <= 0 || percentageValue > 100) {
                newErrors.discountValue = "O percentual de desconto deve estar entre 1% e 100%";
            }
        } else {
            const fixedValue = parseFloat(formValues.discountValue.replace(/\D/g, '')) / 100;
            if (fixedValue <= 0) {
                newErrors.discountValue = "O valor de desconto deve ser maior que zero";
            }
        }

        if (formValues.hasExpiration) {
            const start = new Date(formValues.startDate);
            const expiration = new Date(formValues.expirationDate);
            const today = new Date();

            if (start > expiration) {
                newErrors.expirationDate = "A data de expiração deve ser posterior à data de início";
            }

            if (expiration < today) {
                newErrors.expirationDate = "A data de expiração não pode ser anterior à data atual";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Converter valores de preço de string para número
            const minPriceValue = parseFloat(formValues.minValue.replace(/\D/g, '')) / 100;
            const discountValue = parseFloat(formValues.discountValue.replace(/\D/g, '')) / 100;

            // Preparar dados para envio seguindo o formato esperado pela API
            const couponData = {
                code: formValues.code,
                description: formValues.description,
                productId: productId || formValues.productId,
                global: formValues.global,
                offerList: formValues.global ? [] : formValues.offerList,
                minValue: minPriceValue || 0,
                availableCoupons: parseInt(formValues.availableCoupons) || 0,
                discountType: formValues.discountType,
                discountValue: formValues.discountType === 'fixed' ? discountValue : parseInt(formValues.discountValue),
                active: formValues.active,
                startDate: formValues.hasExpiration ? formValues.startDate : null,
                expirationDate: formValues.hasExpiration ? formValues.expirationDate : null
            };

            // Enviar dados para a API
            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/coupons/${couponId}`,
                couponData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data.success) {
                setApiSuccess("Cupom atualizado com sucesso!");

                // Chamar o callback de sucesso, se fornecido
                if (onSuccess) {
                    onSuccess(response.data.data);
                }

                // Redirecionar após o sucesso
                setTimeout(() => {
                    handleGoBack();
                }, 1500);
            } else {
                setApiError(response.data.error || "Erro ao atualizar cupom");

                // Chamar o callback de erro, se fornecido
                if (onError) {
                    onError(response.data.error);
                }
            }
        } catch (error: any) {
            console.error("Erro ao atualizar cupom:", error);
            setApiError(error.response?.data?.error || error.message || "Erro ao atualizar cupom");

            // Chamar o callback de erro, se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Função para lidar com mudanças no input de desconto
    const handleDiscountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Remove formatação para armazenar apenas o valor numérico
        const numericValue = inputValue.replace(/[^\d]/g, '');

        if (formValues.discountType === 'fixed') {
            // Para valor fixo, converte para formato monetário
            // Armazena o valor como string para preservar casas decimais
            setFormValues(prev => ({
                ...prev,
                discountValue: numericValue
            }));
        } else {
            // Para percentual, limita a 100%
            let percentValue = parseInt(numericValue, 10);
            if (percentValue > 100) percentValue = 100;
            setFormValues(prev => ({
                ...prev,
                discountValue: percentValue.toString()
            }));
        }

        // Limpar erros quando o campo é editado
        if (errors.discountValue) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.discountValue;
                return newErrors;
            });
        }
    };

    // Função para exibir o valor formatado no input
    const getFormattedDiscountValue = () => {
        const numericValue = parseFloat(formValues.discountValue);

        if (isNaN(numericValue)) return formValues.discountType === 'fixed' ? 'R$ 0,00' : '0%';

        if (formValues.discountType === 'fixed') {
            // Formata como moeda (R$)
            return formatCurrency(numericValue / 100); // Divide por 100 para converter centavos para reais
        } else {
            // Formata como percentual
            return `${numericValue}%`;
        }
    };

    // Função para lidar com mudanças no input de valor mínimo
    const handleMinValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Remove formatação para armazenar apenas o valor numérico
        const numericValue = inputValue.replace(/[^\d]/g, '');

        // Armazena o valor como string para preservar casas decimais
        setFormValues(prev => ({
            ...prev,
            minValue: numericValue
        }));

        // Limpar erros quando o campo é editado
        if (errors.minValue) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.minValue;
                return newErrors;
            });
        }
    };

    // Função para exibir o valor mínimo formatado
    const getFormattedMinValue = () => {
        const numericValue = parseFloat(formValues.minValue);

        if (isNaN(numericValue)) return 'R$ 0,00';

        // Formata como moeda (R$)
        return formatCurrency(numericValue / 100); // Divide por 100 para converter centavos para reais
    };

    // Função para gerenciar a seleção de múltiplas ofertas
    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');

        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormValues(prev => ({
                ...prev,
                global: true,
                offerList: []
            }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormValues(prev => ({
                ...prev,
                global: false,
                offerList: values
            }));
        }
    };

    useEffect(() => {
        const fetchCoupon = async () => {
            if (!couponId) {
                setLoadingCoupon(false);
                return;
            }

            try {
                setLoadingCoupon(true);

                const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/coupons/${couponId}`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data.success) {
                    // A API pode retornar os dados em data.data ou data.coupon
                    const couponData = response.data.data || response.data.coupon;
                    
                    if (!couponData) {
                        console.error("Dados do cupom não encontrados na resposta da API");
                        return;
                    }
                    
                    console.log("Dados do cupom recebidos:", couponData);

                    // Definir valores padrão para campos que podem não existir
                    setFormValues({
                        active: couponData.active ?? true,
                        code: couponData.code || '',
                        description: couponData.description || '',
                        minValue: couponData.minValue ? (couponData.minValue * 100).toString() : '0',
                        availableCoupons: couponData.availableCoupons?.toString() || '0',
                        hasExpiration: !!couponData.startDate,
                        startDate: couponData.startDate || new Date().toISOString().split('T')[0],
                        expirationDate: couponData.expirationDate || new Date().toISOString().split('T')[0],
                        discountType: couponData.discountType || 'fixed',
                        discountValue: couponData.discountValue ? (couponData.discountValue * 100).toString() : '0',
                        productId: couponData.productId || productId || '',
                        offerId: couponData.offerId || '',
                        global: couponData.global ?? true,
                        offerList: couponData.offerList || couponData.offersList || []
                    });

                    if (!couponData.global) {
                        setSelectedOffers(couponData.offerList || couponData.offersList || []);
                    } else {
                        setSelectAllOffers(true);
                    }
                } else {
                    console.error("Erro ao buscar cupom:", response.data.error);
                    setApiError(response.data.error || "Erro ao carregar dados do cupom");
                }
            } catch (error) {
                console.error("Erro ao carregar cupom:", error);
                setApiError("Erro ao carregar dados do cupom.");
            } finally {
                setLoadingCoupon(false);
            }
        };

        fetchCoupon();
    }, [couponId, productId]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Editar Cupom">
                    {loadingCoupon ? (
                        <div className="p-6 flex justify-center items-center">
                            <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full mr-3"></div>
                            <span>Carregando dados do cupom...</span>
                        </div>
                    ) : (
                        <>
                            {/* Conteúdo atual aqui */}
                            {apiError && (
                                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg dark:bg-red-900/10 dark:text-red-400">
                                    {apiError}
                                </div>
                            )}

                            {apiSuccess && (
                                <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg dark:bg-green-900/10 dark:text-green-400">
                                    {apiSuccess}
                                </div>
                            )}

                            <div>
                                <div className="col-span-1">
                                    <Switch
                                        label="Ativar Cupom"
                                        checked={formValues.active}
                                        onChange={(checked) => handleSwitchChange("active", checked)}
                                    />
                                </div>
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="code">Código *</Label>
                                <div className="w-full flex items-stretch">
                                    <div className="flex-1 mr-2">
                                        <Input
                                            id="code"
                                            name="code"
                                            placeholder="Digite um código ou clique em gerar"
                                            value={formValues.code}
                                            onChange={handleInputChange}
                                            className="w-full"
                                        />
                                    </div>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={generateRandomCode}
                                    >
                                        Gerar
                                    </Button>
                                </div>
                                {errors.code && (
                                    <span className="text-red-500 text-sm mt-1">{errors.code}</span>
                                )}
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="description">Descrição (opcional)</Label>
                                <Input
                                    type="text"
                                    id="description"
                                    name="description"
                                    placeholder="Informe uma descrição para identificação"
                                    value={formValues.description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="mt-5">
                                <Label>Oferta*</Label>

                                {loadingOffers ? (
                                    <div className="mt-2 flex items-center text-sm text-gray-500">
                                        <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                        Carregando ofertas...
                                    </div>
                                ) : (
                                    <div className="mt-2">
                                        <MultiSelectCheckbox
                                            options={[
                                                {
                                                    id: 'all',
                                                    label: 'Todas as ofertas desse produto',
                                                    disabled: false
                                                },
                                                ...productOffers.filter(offer => offer.value !== 'any').map(offer => ({
                                                    id: offer.value,
                                                    label: `${offer.label}`,
                                                    disabled: false,
                                                    meta: offer
                                                }))
                                            ]}
                                            selectedValues={selectAllOffers ? ['all'] : selectedOffers}
                                            onChange={handleOffersChange}
                                            placeholder="Selecione as ofertas"
                                            error={!!errors.offers}
                                            hint={errors.offers}
                                            searchPlaceholder="Pesquisar ofertas"
                                            showSelectAll={false}
                                        />
                                        {selectAllOffers &&
                                            <p className="mt-1 text-xs text-blue-500">
                                                Este cupom será válido para todas as ofertas do produto, incluindo as futuras e existentes.
                                            </p>
                                        }
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                                <div>
                                    <Label htmlFor="minValue">Valor mínimo da compra</Label>
                                    <Input
                                        id="minValue"
                                        name="minValue"
                                        type="text"
                                        value={getFormattedMinValue()}
                                        onChange={handleMinValueChange}
                                        error={!!errors.minValue}
                                        hint={errors.minValue || "Deixe vazio ou 0 para qualquer valor"}
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="availableCoupons">Nº de cupons disponíveis</Label>
                                    <Input
                                        id="availableCoupons"
                                        name="availableCoupons"
                                        type="number"
                                        value={formValues.availableCoupons}
                                        onChange={handleInputChange}
                                        error={!!errors.availableCoupons}
                                        hint={errors.availableCoupons || "Deixe vazio ou 0 para cupons ilimitados"}
                                    />
                                </div>
                            </div>

                            <div className="mt-5">
                                <Checkbox
                                    id="hasExpiration"
                                    checked={formValues.hasExpiration}
                                    onChange={handleCheckboxChange}
                                    label="Definir data de expiração"
                                />
                            </div>

                            {formValues.hasExpiration && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                                    <div>
                                        <DatePicker
                                            id="startDate"
                                            label="Data de início"
                                            defaultDate={formValues.startDate}
                                            onChange={(selectedDates) => {
                                                const dateStr = selectedDates[0] ? selectedDates[0].toISOString().split('T')[0] : '';
                                                setFormValues(prev => ({ ...prev, startDate: dateStr }));
                                            }}
                                            placeholder="Selecionar data de início"
                                        />
                                    </div>

                                    <div>
                                        <DatePicker
                                            id="expirationDate"
                                            label="Data de expiração"
                                            defaultDate={formValues.expirationDate}
                                            onChange={(selectedDates) => {
                                                const dateStr = selectedDates[0] ? selectedDates[0].toISOString().split('T')[0] : '';
                                                setFormValues(prev => ({ ...prev, expirationDate: dateStr }));

                                                // Validar a data de expiração
                                                if (formValues.startDate && dateStr) {
                                                    const startDate = new Date(formValues.startDate);
                                                    const expDate = new Date(dateStr);

                                                    if (expDate <= startDate) {
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            expirationDate: 'A data de expiração deve ser posterior à data de início'
                                                        }));
                                                    } else {
                                                        setErrors(prev => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors.expirationDate;
                                                            return newErrors;
                                                        });
                                                    }
                                                }
                                            }}
                                            placeholder="Selecionar data de expiração"
                                        />
                                        {errors.expirationDate && (
                                            <span className="text-sm text-red-500">{errors.expirationDate}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="mt-8">
                                <Label>Selecione o tipo de desconto</Label>
                                <div className="flex flex-wrap gap-4 mt-6">
                                    <div className="flex items-center">
                                        <input
                                            id="fixed-option"
                                            name="discount-type"
                                            type="radio"
                                            checked={formValues.discountType === "fixed"}
                                            onChange={() => handleDiscountTypeChange("fixed")}
                                            className="w-5 h-5 text-brand-600 border-gray-300 focus:ring-brand-500"
                                        />
                                        <label
                                            htmlFor="fixed-option"
                                            className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Valor Fixo
                                        </label>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            id="percentage-option"
                                            name="discount-type"
                                            type="radio"
                                            checked={formValues.discountType === "percentage"}
                                            onChange={() => handleDiscountTypeChange("percentage")}
                                            className="w-5 h-5 text-brand-600 border-gray-300 focus:ring-brand-500"
                                        />
                                        <label
                                            htmlFor="percentage-option"
                                            className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                                        >
                                            Percentual
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="discountValue">
                                    {formValues.discountType === 'fixed'
                                        ? 'Valor do desconto'
                                        : 'Percentual de desconto (%)'}
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="discountValue"
                                        name="discountValue"
                                        type="text"
                                        value={getFormattedDiscountValue()}
                                        onChange={handleDiscountValueChange}
                                        error={!!errors.discountValue}
                                        hint={errors.discountValue}
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                                        <span className="text-gray-500 text-sm">
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-4.5">
                                <Button
                                    size="md"
                                    variant="outline"
                                    onClick={handleGoBack}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => handleSubmit()}
                                    disabled={loading}
                                >
                                    {loading ? "Atualizando..." : "Atualizar Cupom"}
                                </Button>
                            </div>        </>
                    )}
                </ComponentCard>
            </div>
        </div>
    );
}